import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

const articleImageMap: Record<string, string> = {
  'article-chengshi-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-chengshi-detail&orientation=landscape',
  'article-mama-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-mama-detail&orientation=landscape',
  'article-kaitou-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-kaitou-detail&orientation=landscape',
  'article-xijie-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-xijie-detail&orientation=landscape',
  'article-qinggan-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-qinggan-detail&orientation=landscape',
  'article-baba-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-baba-detail&orientation=landscape',
  'article-ganwu-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-ganwu-detail&orientation=landscape',
  'article-zhenqing-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-zhenqing-detail&orientation=landscape',
  'article-jiegou-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-jiegou-detail&orientation=landscape',
  'article-zeren-detail': 'https://readdy.ai/api/search-image?query=Warm%20Chinese%20study%20room%20with%20books%20and%20notebooks%2C%20soft%20natural%20light%2C%20educational%20atmosphere%2C%20calligraphy%20tools%2C%20peaceful%20reading%20environment&width=1200&height=675&seq=article-zeren-detail&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center py-16">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
              <i className="ri-file-search-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const detailImageUrl = articleImageMap[`${article.imageSeq}-detail`] || articleImageMap['article-chengshi-detail'];

  const articleSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `https://zuowenjiqiao.com/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: '写作技巧大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '写作技巧大全',
    },
  });

  return (
    <>
      <title>{article.title} - 写作技巧大全</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <link rel="canonical" href={`https://zuowenjiqiao.com/article/${article.slug}`} />
      <meta property="og:title" content={`${article.title} - 写作技巧大全`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://zuowenjiqiao.com/article/${article.slug}`} />
      <script type="application/ld+json">{articleSchema}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10 md:pb-14 px-4 md:px-6">
          <article className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            <header className="mb-8">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4 hover:bg-accent-200 transition-colors"
              >
                {article.categoryName}
              </Link>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
                <span><i className="ri-eye-line mr-1"></i>阅读 2.3k</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={detailImageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="text-sm text-foreground-600">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="text-lg font-heading text-foreground-900 mb-4">
                    相关文章推荐
                  </h3>
                  <div className="space-y-3">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.slug} article={ra} variant="compact" />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 text-center">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 border border-background-200/70 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i> 返回{article.categoryName}
                </Link>
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}